/** Constantes partagées. Toute limite appliquée côté client l'est aussi côté serveur. */
/** Taille maximale d'un fichier source, en octets (PRD FR-9). */
export const MAX_VIDEO_SIZE_BYTES = 20 * 1024 * 1024 * 1024; // 20 Go
/** Taille maximale d'un artefact de modèle, en octets (ADR-0014). Large marge : un ONNX de
 *  détection dépasse rarement quelques centaines de Mo. */
export const MAX_MODEL_SIZE_BYTES = 2 * 1024 * 1024 * 1024; // 2 Go
/**
 * Taille d'une part d'upload multipart, en octets.
 * 100 Mio place un fichier de 20 Go à 205 parts, sous la limite S3 de 10 000 parts,
 * tout en gardant une part assez petite pour qu'une reprise ne coûte pas cher.
 */
export const UPLOAD_PART_SIZE_BYTES = 100 * 1024 * 1024;
/** Uploads simultanés côté navigateur — au-delà, on sature le lien montant sans rien gagner. */
export const MAX_CONCURRENT_UPLOADS = 3;
/** Durée de validité d'une URL pré-signée de lecture, en secondes (PRD NFR-5). */
export const PLAYBACK_URL_TTL_SECONDS = 300;
/**
 * Durée de validité d'une **session de lecture HLS**, en secondes (jeton de lecture ET URLs
 * pré-signées des segments). Volontairement longue : une playlist VOD liste tous ses segments
 * d'emblée et le lecteur (hls.js / HLS natif) ne la recharge pas — les URLs des segments doivent
 * donc rester valides toute la durée du visionnage, y compris si l'on revient en arrière. 4 h
 * couvre largement une revue d'annotation, sans faire des liens quasi perpétuels.
 */
export const HLS_PLAYBACK_TTL_SECONDS = 4 * 3600;
/** Durée de validité d'une URL pré-signée d'upload, en secondes. */
export const UPLOAD_URL_TTL_SECONDS = 3600;
/**
 * Conteneurs acceptés à l'upload. La liste ne filtre **que le conteneur**, jamais le codec : le
 * worker ré-encode tout en H.264 HLS (ADR-0007), donc un H.265/HEVC, du VP9, etc. sont pris en
 * charge du moment que le conteneur est reconnu. Le type réel est **revérifié par ffprobe** côté
 * worker — c'est lui le vrai garde ; cette liste sert surtout le confort de sélection au navigateur.
 */
export const ACCEPTED_VIDEO_MIME_TYPES = [
    'video/mp4',
    'video/quicktime', // .mov
    'video/x-matroska', // .mkv
    'video/webm',
    'video/x-msvideo', // .avi
    'video/mp2t', // MPEG-TS : .ts / .mts / .m2ts (fréquent sur GoPro, caméscopes AVCHD)
    // Flux HEVC brut (Annex-B), sans conteneur — .h265 / .hevc, produit par certains enregistreurs
    // drone. Pas de type MIME officiellement enregistré ; `video/hevc` est l'usage de facto. ffmpeg
    // détecte le flux à la sonde et le transcode comme les autres (le worker revérifie par ffprobe).
    'video/hevc',
];
export const ACCEPTED_VIDEO_EXTENSIONS = [
    '.mp4',
    '.mov',
    '.mkv',
    '.webm',
    '.avi',
    '.ts',
    '.mts',
    '.m2ts',
    '.h265',
    '.hevc',
];
/**
 * Extension (en minuscules, point compris) → MIME canonique. **Source unique** partagée par le front
 * (qui déclare le type à l'upload) et l'API. Indispensable car les navigateurs renseignent mal
 * `File.type` pour les conteneurs peu courants (souvent vide ou `application/octet-stream`).
 */
export const VIDEO_EXTENSION_MIME = {
    '.mp4': 'video/mp4',
    '.mov': 'video/quicktime',
    '.mkv': 'video/x-matroska',
    '.webm': 'video/webm',
    '.avi': 'video/x-msvideo',
    '.ts': 'video/mp2t',
    '.mts': 'video/mp2t',
    '.m2ts': 'video/mp2t',
    // Flux HEVC brut : les navigateurs ne renseignent pas `File.type`, on dérive de l'extension.
    '.h265': 'video/hevc',
    '.hevc': 'video/hevc',
};
/**
 * MIME vidéo canonique d'un fichier : le type déclaré s'il fait partie des types reconnus, sinon
 * **dérivé de l'extension** (le navigateur ne renseigne pas toujours `File.type`). `null` si
 * l'extension n'est pas un conteneur accepté — le worker revérifie de toute façon par ffprobe.
 */
export function canonicalVideoMime(filename, declared) {
    if (declared && ACCEPTED_VIDEO_MIME_TYPES.includes(declared)) {
        return declared;
    }
    const dot = filename.lastIndexOf('.');
    const ext = dot >= 0 ? filename.slice(dot).toLowerCase() : '';
    return VIDEO_EXTENSION_MIME[ext] ?? null;
}
/** Extensions de fichiers de télémétrie reconnues. */
export const ACCEPTED_TELEMETRY_EXTENSIONS = ['.srt', '.csv'];
/** Variantes HLS produites au transcodage (ADR-0007). */
export const HLS_VARIANTS = [
    { height: 1080, width: 1920, videoBitrateKbps: 5000, audioBitrateKbps: 128 },
    { height: 720, width: 1280, videoBitrateKbps: 2800, audioBitrateKbps: 128 },
    { height: 480, width: 854, videoBitrateKbps: 1400, audioBitrateKbps: 96 },
];
/** Durée d'un segment HLS, en secondes. */
export const HLS_SEGMENT_DURATION_SECONDS = 6;
/**
 * Rétention par défaut d'un élément en corbeille, en jours (≈ 6 mois). Passé ce délai, l'élément
 * est purgé définitivement. Configurable par organisation (`retentionDays`).
 */
export const DEFAULT_TRASH_RETENTION_DAYS = 180;
/** Bornes de la rétention configurable, en jours (7 jours à 5 ans). */
export const MIN_TRASH_RETENTION_DAYS = 7;
export const MAX_TRASH_RETENTION_DAYS = 1825;
/**
 * Rétention des événements d'analytique d'usage bruts, en jours (ADR-0026 §4). Au-delà, ils sont
 * purgés ; seules les vues agrégées (non ré-identifiables) survivent. Plus courte que la corbeille :
 * la donnée d'usage se démode et sa conservation est un risque, pas un actif.
 */
export const ANALYTICS_RETENTION_DAYS = 180;
/** Nombre maximal d'événements par lot d'ingestion (ADR-0026 §3 : amortir le chemin critique). */
export const ANALYTICS_BATCH_MAX_EVENTS = 100;
/** Durée de vie par défaut d'un lien de partage, en jours (PRD FR-31). */
export const DEFAULT_SHARE_EXPIRY_DAYS = 30;
/** Durée de vie maximale d'un lien de partage, en jours (PRD FR-31). */
export const MAX_SHARE_EXPIRY_DAYS = 365;
/** Taille de page par défaut (PRD FR-22). */
export const DEFAULT_PAGE_SIZE = 24;
export const MAX_PAGE_SIZE = 100;
/** Longueur minimale d'un mot de passe (docs/security.md §2). */
export const MIN_PASSWORD_LENGTH = 12;
/**
 * Seuil de confiance par défaut sous lequel une prédiction du modèle est écartée (ADR-0013).
 * Modéré : assez bas pour proposer, assez haut pour ne pas noyer l'utilisateur de faux positifs.
 * Surchargeable côté worker (`INFERENCE_CONFIDENCE_THRESHOLD`).
 */
export const DEFAULT_INFERENCE_CONFIDENCE_THRESHOLD = 0.35;
/** Préfixes des objets dans le bucket média (ADR-0004). */
export const storageKeys = {
    source: (orgId, videoId, filename) => `${orgId}/${videoId}/source/${filename}`,
    hlsMaster: (orgId, videoId) => `${orgId}/${videoId}/hls/master.m3u8`,
    hlsVariant: (orgId, videoId, height) => `${orgId}/${videoId}/hls/${height}/index.m3u8`,
    hlsPrefix: (orgId, videoId) => `${orgId}/${videoId}/hls/`,
    cover: (orgId, videoId) => `${orgId}/${videoId}/thumbs/cover.jpg`,
    sprite: (orgId, videoId) => `${orgId}/${videoId}/thumbs/sprite.jpg`,
    spriteVtt: (orgId, videoId) => `${orgId}/${videoId}/thumbs/sprite.vtt`,
    telemetry: (orgId, videoId, filename) => `${orgId}/${videoId}/telemetry/${filename}`,
    /** Image fixe extraite d'une vidéo (PNG), déposée par le navigateur (ADR-0010). */
    frame: (orgId, videoId, imageId) => `${orgId}/${videoId}/frames/${imageId}.png`,
    /** Vignette (JPEG réduit) de l'image extraite, pour des aperçus légers dans les listes. */
    frameThumb: (orgId, videoId, imageId) => `${orgId}/${videoId}/frames/${imageId}_thumb.jpg`,
    /** Tout ce qui appartient à une vidéo — utilisé à la suppression. */
    videoPrefix: (orgId, videoId) => `${orgId}/${videoId}/`,
    /** Artefact d'un modèle enregistré (ADR-0014). Hors arborescence vidéo : indexé par organisation. */
    model: (orgId, modelId, filename) => `${orgId}/models/${modelId}/${filename}`,
    /** Photo d'un build de matériel (ADR-0032), déposée par le navigateur. Sous le préfixe du matériel. */
    hardwarePhoto: (orgId, assetId, photoId, ext) => `${orgId}/hardware/${assetId}/photos/${photoId}.${ext}`,
    /**
     * Illustration d'une pièce du catalogue (ADR-0032). Le catalogue est **global** (pas de préfixe
     * organisation). **Sans extension** : le type est porté par l'objet, et remplacer l'image réécrit
     * la même clé.
     */
    catalogMedia: (partId) => `catalog/${partId}/media`,
};
/** Types d'image acceptés pour une illustration de pièce du catalogue (ADR-0032). */
export const CATALOG_IMAGE_CONTENT_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
/** Taille maximale d'une illustration de pièce (5 Mo), bornée à l'import comme à l'upload (ADR-0032). */
export const CATALOG_IMAGE_MAX_BYTES = 5 * 1024 * 1024;
//# sourceMappingURL=constants.js.map